import './ItemCard.scss';
import React, { type ReactNode } from 'react';
import cx from 'classnames';
import { Link } from 'react-router';
import IconButton from '../iconButton/IconButton';
import { useTranslation } from 'react-i18next';
import ItemTeaser from '../itemTeaser/ItemTeaser';
import { v4 as uuidv4 } from 'uuid';
import { SortableKnob } from "react-easy-sort";

export type ActionButtonType = {
    icon: string;
    onClick: () => void;
    disabled?: boolean;
    isActive?: boolean;
};

type RouterLinkType = {
    pathname: string;
    query: { [key: string]: string };
};

type Props = {
    buttons?: Array<ActionButtonType | Element | React.ReactNode | any>;
    className?: string;
    description?: string;
    footer?: ReactNode | string;
    href?: string | RouterLinkType;
    icon?: string;
    isGridStyle?: boolean;
    isLoading?: boolean;
    isDisabled?: boolean;
    onClick?: () => void;
    onImageLoad?: () => void;
    overlayTag?: ReactNode;
    subtitle?: string | ReactNode;
    /**
     * thumbnail: can be image or video.
     * If video link, video can be preview played. See ItemTeaser,
     */
    thumbnail?: string | undefined;
    videoSource?: string;
    title?: string;
    name?: string;
    type?: 'video' | 'image' | any;
    divProps?: React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLDivElement>,
        HTMLDivElement
    >;
    withSortableKnob?: boolean;
};

const ItemCard = ({
    buttons,
    className,
    description,
    footer,
    href,
    icon,
    isDisabled,
    isGridStyle,
    isLoading,
    onClick,
    onImageLoad,
    overlayTag,
    subtitle,
    thumbnail,
    title,
    type = 'image',
    videoSource,
    name,
    withSortableKnob = false,
    ...divProps
}: Props) => {
    const { t } = useTranslation();
    return (
        <div
            className={cx(
                'b-item-card',
                {
                    'is-grid': isGridStyle,
                    'is-disabled': isDisabled
                },
                className
            )}
            {...divProps}
            data-testid={`item-card-${name}`}
        >
            {withSortableKnob ? (
                <SortableKnob>
                    <div className="b-item-card__link-to-asset" onClick={onClick}>
                        <div key="preview" className="b-item-card__item-teaser">
                            <ItemTeaser
                                alt={title}
                                icon={icon}
                                onLoad={onImageLoad}
                                overlayTag={overlayTag}
                                src={thumbnail}
                                isLoading={isLoading}
                                type={type}
                                videoSource={videoSource}
                            />
                        </div>
                        <div key="info" className="b-item-card__info-container">
                            <Link to={href as any} data-testid={`item-card-link-${name}`}>
                                <h4 className="b-item-card__title" data-testid={`item-card-title-${name}`}>
                                    {title || t('no_title')}
                                </h4>
                            </Link>
                            {subtitle && (
                                <div className="b-item-card__subtitle" data-testid={`item-card-subtitle-${name}`}>{subtitle}</div>
                            )}
                            <div className="b-item-card__description" data-testid={`item-card-description-${name}`}>
                                {`${t('description')}: ${
                                    description || t('no_description')
                                }`}
                            </div>
                            <div className="b-item-card__footer" data-testid={`item-card-footer-${name}`}>{footer}</div>
                        </div>
                    </div>
                </SortableKnob>
            ) : (
                <div className="b-item-card__link-to-asset" onClick={onClick}>
                    <div key="preview" className="b-item-card__item-teaser">
                        <ItemTeaser
                            alt={title}
                            icon={icon}
                            onLoad={onImageLoad}
                            overlayTag={overlayTag}
                            src={thumbnail}
                            isLoading={isLoading}
                            type={type}
                            videoSource={videoSource}
                        />
                    </div>
                    <div key="info" className="b-item-card__info-container">
                        <Link to={href as any} data-testid={`item-card-link-${name}`}>
                            <h4 className="b-item-card__title" data-testid={`item-card-title-${name}`}>
                                {title || t('no_title')}
                            </h4>
                        </Link>
                        {subtitle && (
                            <div className="b-item-card__subtitle" data-testid={`item-card-subtitle-${name}`}>{subtitle}</div>
                        )}
                        <div className="b-item-card__description" data-testid={`item-card-description-${name}`}>
                                {`${t('description')}: ${
                                    description || t('no_description')
                                }`}
                            </div>
                        <div className="b-item-card__footer" data-testid={`item-card-footer-${name}`}>{footer}</div>
                    </div>
                </div>
            )}
            {buttons && (
                <div className={'b-item-card__button-list'}>
                    {buttons.map(button => {
                        if ('icon' in button) {
                            return (
                                <IconButton
                                    key={uuidv4()}
                                    onClick={button.onClick}
                                    icon={button.icon}
                                    disabled={button.disabled}
                                    color={button.isActive ? 'black' : ''}
                                    name={button.name}
                                />
                            );
                        }
                        return button;
                    })}
                </div>
            )}
        </div>
    );
};

export default ItemCard;
